import { MAX_CUPS, MAX_STARS } from '@shared/progression';
import { MAX_CROWN_RANK } from '@shared/seasons';

export interface TrophyStripProps {
  stars: number;
  cups: number;
  /**
   * Live season crowns held on this map (0–3, from the player's current
   * podium rank: #1 → 3, #2 → 2, #3 → 1). Defaults to 0. Unlike stars/cups
   * the crown row only ever renders when > 0 — there's no "empty crown"
   * slot, because a crown reflects a live standing, not collectable mastery.
   */
  crowns?: number;
  /**
   * Visual size variant. `sm` is the compact homepage row chip; `md`
   * (default) is used everywhere else, including the results overlay.
   */
  size?: 'sm' | 'md';
  /**
   * If true, render the stars/cups rows even when both counts are 0 (used
   * when we want to preview "0 / 3" — e.g. inside a celebration card).
   * Default false: the homepage row hides the strip entirely for fresh maps.
   * Never forces the crown row, which always hides at 0.
   */
  showWhenEmpty?: boolean;
  className?: string;
}

/**
 * Best-ever stars + cups (plus live season crowns) for a single map.
 * Renders nothing when every count is 0 unless `showWhenEmpty` is set —
 * the homepage-display rule the product spec asked for ("There are none?
 * Don't show anything").
 *
 * Stars, cups, and crowns each render as their own row, top to bottom in
 * canonical currency order. A player who only has stars sees a single
 * line; the cup row appears once they break into infinite-mode loops, and
 * the crown row appears whenever they're sitting top 3 on the map.
 */
export function TrophyStrip({
  stars,
  cups,
  crowns = 0,
  size = 'md',
  showWhenEmpty = false,
  className,
}: TrophyStripProps) {
  const crownCount = Math.min(Math.max(crowns, 0), MAX_CROWN_RANK);
  if (!showWhenEmpty && stars <= 0 && cups <= 0 && crownCount <= 0) {
    return null;
  }
  const showStars = showWhenEmpty || stars > 0;
  const showCups = showWhenEmpty || cups > 0;
  const showCrowns = crownCount > 0;
  // Only announce the rows that actually render, so a strip showing just a
  // crown (no stars/cups) doesn't read out "0 of 3 stars" to a screen reader.
  const ariaParts: string[] = [];
  if (showStars) {
    ariaParts.push(`${stars} of ${MAX_STARS} stars`);
  }
  if (showCups) {
    ariaParts.push(`${cups} of ${MAX_CUPS} cups`);
  }
  if (showCrowns) {
    ariaParts.push(`${crownCount} season crown${crownCount === 1 ? '' : 's'}`);
  }
  return (
    <div
      className={`trophy-strip trophy-strip-${size}${className ? ` ${className}` : ''}`}
      data-testid="trophy-strip"
      aria-label={ariaParts.join(', ')}
    >
      {showStars && (
        <div
          className={`trophy-row trophy-row-stars${stars >= MAX_STARS ? ' trophy-row-max' : ''}`}
          data-testid="trophy-stars"
        >
          {Array.from({ length: MAX_STARS }, (_, i) => (
            <span
              key={i}
              className={`trophy-icon trophy-star${i < stars ? ' trophy-icon-on' : ''}`}
              aria-hidden="true"
            >
              {i < stars ? '\u2605' : '\u2606'}
            </span>
          ))}
        </div>
      )}
      {showCups && (
        <div
          className={`trophy-row trophy-row-cups${cups >= MAX_CUPS ? ' trophy-row-max' : ''}`}
          data-testid="trophy-cups"
        >
          {Array.from({ length: MAX_CUPS }, (_, i) => (
            <span
              key={i}
              className={`trophy-icon trophy-cup${i < cups ? ' trophy-icon-on' : ''}`}
              aria-hidden="true"
            >
              {/* Trophy-cup glyph. Empty state uses a hollow circle so
                  the row reads "earned vs unearned" at a glance. */}
              {i < cups ? '\u{1F3C6}' : '\u25CB'}
            </span>
          ))}
        </div>
      )}
      {showCrowns && (
        <div
          className={`trophy-row trophy-row-crowns${crownCount >= MAX_CROWN_RANK ? ' trophy-row-max' : ''}`}
          data-testid="trophy-crowns"
        >
          {/* Filled-only: crowns reflect a live podium standing (#1 → 3,
              #2 → 2, #3 → 1), not collectable mastery, so there are no
              empty slots — the row simply appears once you're top 3. */}
          {Array.from({ length: crownCount }, (_, i) => (
            <span
              key={i}
              className="trophy-icon trophy-crown trophy-icon-on"
              aria-hidden="true"
            >
              {'\u{1F451}'}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
